// Import Dependencies
import React, { useState, useEffect, Fragment } from "react";
import { useParams, useNavigate } from "react-router";
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import { getFormByIdWebhook, submitFormWebhook, transformAPIFormToPreviewFormat, sendPhoneOTP, verifyPhoneOTP } from "services/formBuilderService";
import sunnyHomesCrmLogo from "assets/images/SH-logo.png"
// Import Phone Input
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import enLocale from 'react-phone-number-input/locale/en.json';
// Import scrollbar styles
import "../../../styles/scrollbar.css";
// Import custom styles for FormPage
import "./FormPage.css";

// UI Components
import { Button, Input, Textarea, Select, Checkbox, Radio } from "components/ui";
import { AttachmentsActions } from "components/shared/AttachmentsActions";
import RichTextEditor from "components/shared/RichTextEditor";
import ReactSelect from "react-select";

// Toast notifications
import { toast } from "sonner";
import { JWT_HOST_API } from "configs/auth.config";
import { CONTACT_FIELD_NAMES, CONTACT_SCHEMAS } from "constants/contactInfoFields";
import { formatABN } from "utils/formatABN";

// Import Validation

// ----------------------------------------------------------------------

const groupContactFields = (fields) => {
  if (!fields || fields.length === 0) return [];

  const contactFieldsFound = [];
  const nonContactFields = [];
  let firstContactIndex = -1;

  fields.forEach((field, index) => {
    const fieldName = field.fieldName || field.FieldName;
    if (fieldName && CONTACT_FIELD_NAMES.includes(fieldName.toLowerCase())) {
      contactFieldsFound.push(field);
      if (firstContactIndex === -1) {
        firstContactIndex = index;
      }
    } else {
      nonContactFields.push(field);
    }
  });

  if (contactFieldsFound.length === 0) {
    return fields;
  }


  const groupedFields = CONTACT_SCHEMAS
    .filter(schema => contactFieldsFound.some(f => (f.fieldName || f.FieldName || '').toLowerCase() === schema.name))
    .map(schema => {
      const existing = contactFieldsFound.find(f => (f.fieldName || f.FieldName || '').toLowerCase() === schema.name);
      const isMandatoryVal = schema.required || !!existing.isRequired || !!existing.required || !!existing.isMandatory || !!existing.IsMendatory;
      return {
        ...existing,
        label: existing.label || existing.fieldLabel || schema.label,
        fieldLabel: existing.fieldLabel || existing.label || schema.label,
        required: isMandatoryVal,
        isRequired: isMandatoryVal,
        isMandatory: !!schema.isMandatory,
        IsMendatory: !!schema.isMandatory
      };
    });

  const contactFieldset = {
    id: "contact_fieldset",
    type: "contact_fieldset",
    label: "Contact Information",
    isMandatory: true,
    IsMendatory: true,
    fields: groupedFields
  };

  const filtered = fields.filter(f => {
    const fn = f.fieldName || f.FieldName;
    return !fn || !CONTACT_FIELD_NAMES.includes(fn.toLowerCase());
  });

  const targetIndex = firstContactIndex !== -1 ? Math.min(firstContactIndex, filtered.length) : 0;
  filtered.splice(targetIndex, 0, contactFieldset);

  return filtered;
};

export function FormPage(props) {
  const { formId: paramFormId, assignTo } = useParams();
  const formId = paramFormId || props.formId;
  const assignToUser = assignTo || props.assignTo;
  // Determine if this render came from a QR route (/qr/:token)
  // QRFormPage passes qrToken as a prop; the /form route never does
  const qrToken = props.qrToken || null;
  const isQR = Boolean(qrToken);
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [formValues, setFormValues] = useState({});
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  const [simulatedContacts, setSimulatedContacts] = useState([0]);

  const getFlatFieldsWithSimulated = (fields, simulatedContactsList) => {
    const flat = [];
    fields.forEach(field => {
      if (field.type === 'contact_fieldset') {
        simulatedContactsList.forEach(contactId => {
          field.fields?.forEach(innerField => {
            const fieldId = contactId === 0 ? innerField.id : `${innerField.id}_${contactId}`;
            flat.push({
              ...innerField,
              id: fieldId,
              masterFieldId: contactId === 0 ? innerField.masterFieldId : (innerField.masterFieldId ? `${innerField.masterFieldId}_${contactId}` : `${innerField.id}_${contactId}`)
            });
          });
        });
      } else {
        flat.push(field);
      }
    });
    return flat;
  };

  const getAllFieldsFlat = () => {
    if (!form) return [];
    const rawFields = form.isMultistep
      ? form.steps?.flatMap(step => step.fields) || []
      : form.fields || [];
    const groupedFields = groupContactFields(rawFields);
    return getFlatFieldsWithSimulated(groupedFields, simulatedContacts);
  };

  // Phone OTP verification states
  const [phoneFieldId, setPhoneFieldId] = useState(null);
  const [isPhoneVerified, setIsPhoneVerified] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState('');
  const [otpId, setOtpId] = useState(null); // Store OtpId from API response
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [otpError, setOtpError] = useState('');
  const [canResendOtp, setCanResendOtp] = useState(true);
  const [resendTimer, setResendTimer] = useState(0);
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [companyLogo, setCompanyLogo] = useState(sunnyHomesCrmLogo);

  // Set document title
  useEffect(() => {
    document.title = "Form";
  }, []);

  // Set company logo from URL
  useEffect(() => {
    const setCompanyLogoFromUrl = () => {
      try {
        const hostname = window.location.hostname;
        let folderName = hostname;

        // Handle localhost development environment
        if (hostname.includes('localhost') || hostname.includes('127.0.0.1')) {
          folderName = 'localhost';
        }

        const apiHost = JWT_HOST_API.endsWith('/') ? JWT_HOST_API : `${JWT_HOST_API}/`;
        const logoUrl = `${apiHost}CompanyLogos/${folderName}/main.png`;

        setCompanyLogo(logoUrl);
      } catch (error) {
        console.error("Failed to set company logo from URL", error);
      }
    };
    setCompanyLogoFromUrl();
  }, []);

  useEffect(() => {
    const fetchForm = async () => {
      try {
        setLoading(true);
        if (!formId) {
          setError('No form ID provided');
          setLoading(false);
          return;
        }

        const result = await getFormByIdWebhook(formId);

        if (result.success && result.data) {
          // Check if form is active before processing
          const isActive = result.data.IsActive !== undefined ? result.data.IsActive : true;

          // Check if data is null/empty (missing FormId)
          if (!result.data.FormId && !result.data.formId) {
            setError('This form is currently unavailable');
            return;
          }

          if (!isActive) {
            setError('This form is currently unavailable');
            return;
          }

          // Use the shared transformation function to match preview format
          const transformedForm = transformAPIFormToPreviewFormat(result.data);
          setForm(transformedForm);
        } else {
          // Handle different error types

          // Check multiple ways a 401/unauthorized could be indicated
          const isUnauthorized =
            result.statusCode === 401 ||
            result.message === "Unauthorised" ||
            result.message === "Unauthorized" ||
            (result.error && result.error.status === 401) ||
            (result.error && result.error.statusCode === 401) ||
            (result.error && (
              result.error.message === "Unauthorised" ||
              result.error.message === "Unauthorized"
            ));

          if (isUnauthorized) {
            setError('access_denied');
          } else {
            setError('Form not found');
          }
        }
      } catch (err) {
        console.error('Error fetching form:', err);

        // Check if the error is a 401 unauthorized error
        const isUnauthorized =
          err.response?.status === 401 ||
          err.response?.statusCode === 401 ||
          err.message?.includes('401') ||
          err.message?.includes('Unauthorised') ||
          err.message?.includes('Unauthorized');

        if (isUnauthorized) {
          setError('access_denied');
        } else {
          setError('Failed to load form: ' + (err.message || 'Unknown error'));
        }
      } finally {
        setLoading(false);
      }
    };

    if (formId) {
      fetchForm();
    } else {
      setError('No form ID found in URL');
      setLoading(false);
    }
  }, [formId]);

  // Find phone field and initialize defaults when form loads
  useEffect(() => {
    if (form) {
      // Find all fields from all steps
      const allFields = getAllFieldsFlat();

      // identify all phone fields by FieldTypeId = 10 or type = tel
      const phoneFields = allFields.filter(field => {
        const fieldTypeId = field.fieldTypeId || field.FieldTypeId;
        return fieldTypeId === 10 || field.type === 'tel';
      });

      if (phoneFields.length > 0) {
        setPhoneFieldId(phoneFields[0].id);
        setFormValues(prev => {
          let updated = false;
          const newValues = { ...prev };
          phoneFields.forEach(pf => {
            if (!newValues[pf.id]) {
              newValues[pf.id] = '+61';
              updated = true;
            }
          });
          return updated ? newValues : prev;
        });
      }

      // Initialize Country default to Australia
      const countryField = allFields.find(f => {
        const lowerLabel = (f.label || f.fieldLabel || '').toLowerCase();
        return lowerLabel === 'country' || lowerLabel === 'countryid';
      });

      if (countryField) {
        // If it's a select/radio field, look for Australia option
        const australiaOption = countryField.options?.find(opt =>
          (opt.label || '').toLowerCase() === 'australia'
        );

        // Use matching option value, or ID, or just 'Australia'
        const defaultValue = australiaOption ? australiaOption.value : 'Australia';

        setFormValues(prev => {
          // Only set if not already set by user
          if (prev[countryField.id]) return prev;
          return {
            ...prev,
            [countryField.id]: defaultValue
          };
        });
      }
    }
  }, [form, simulatedContacts]);

  // Resend OTP timer countdown
  useEffect(() => {
    let interval;
    if (resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer(prev => {
          if (prev <= 1) {
            setCanResendOtp(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [resendTimer]);

  // Handle sending OTP
  const handleSendOtp = async () => {
    const phoneNumber = formValues[phoneFieldId];

    if (!phoneNumber) {
      setOtpError('Please enter a phone number first');
      toast.error('Please enter a phone number');
      return;
    }

    // Validate using the library's robust validation (checks country code + local length)
    if (!isValidPhoneNumber(phoneNumber)) {
      setOtpError('Please enter a valid phone number');
      toast.error('Please enter a valid phone number');
      return;
    }

    // Extract only digits from international format for the API payload
    // e.g., "+91 98765 43210" -> "919876543210"
    const fullPhoneNumber = phoneNumber.replace(/\D/g, '');

    setIsSendingOtp(true);
    setOtpError('');

    try {
      // Pass existing otpId for resend, or empty for first send
      // Send full number with country code
      const result = await sendPhoneOTP(fullPhoneNumber, otpId || '');

      if (result.success) {
        setOtpSent(true);
        setOtpId(result.otpId); // Store the OtpId for verification
        setCanResendOtp(false);
        setResendTimer(30); // 30 seconds cooldown
        setShowOtpModal(true); // Open OTP modal
        toast.success('OTP sent successfully!', {
          description: 'Please check your phone for the verification code.',
          duration: 4000,
        });
      } else {
        setOtpError(result.message || 'Failed to send OTP');
        toast.error(result.message || 'Failed to send OTP');
      }
    } catch (err) {
      setOtpError('Failed to send OTP. Please try again.');
      toast.error('Failed to send OTP');
    } finally {
      setIsSendingOtp(false);
    }
  };

  // Handle verifying OTP
  const handleVerifyOtp = async () => {
    if (!otp || otp.length < 4) {
      setOtpError('Please enter a valid OTP');
      return;
    }

    if (!otpId) {
      setOtpError('Invalid verification session. Please request a new OTP.');
      return;
    }

    setIsVerifyingOtp(true);
    setOtpError('');

    try {
      const result = await verifyPhoneOTP(otpId, otp);

      if (result.success) {
        setIsPhoneVerified(true);
        setOtpSent(false);
        setShowOtpModal(false); // Close modal on success
        toast.success('Phone number verified successfully!', {
          duration: 3000,
        });
      } else {
        setOtpError(result.message || 'Invalid OTP');
        toast.error(result.message || 'Invalid OTP');
      }
    } catch (err) {
      setOtpError('Failed to verify OTP. Please try again.');
      toast.error('Failed to verify OTP');
    } finally {
      setIsVerifyingOtp(false);
    }
  };

  // Validate a single field's value based on its type and isMandatory rules
  const validateFieldValue = (field, value) => {
    if (!field) return null;

    const isRequired = field.required || field.isMandatory || field.IsMendatory || field.isRequired;

    // Check if value is empty
    let isEmpty = false;
    if (field.type === 'checkbox' || field.type === 'attachments') {
      isEmpty = !value || (Array.isArray(value) && value.length === 0);
    } else if (typeof value === 'string') {
      isEmpty = !value.trim();
    } else {
      isEmpty = value === undefined || value === null || value === '';
    }

    if (isEmpty) {
      if (isRequired) {
        return `${field.label || 'Field'} is required`;
      }
      return null;
    }

    // Value is not empty, validate format based on field type
    const fieldTypeId = field.fieldTypeId || field.FieldTypeId;

    // Type 8: Email
    if (fieldTypeId === 8 || field.type === 'email') {
      const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
      if (!emailRegex.test(value)) {
        return "Please enter a valid email address";
      }
    }

    // Type 10: Phone
    if (fieldTypeId === 10 || field.type === 'tel') {
      const phoneVal = String(value);
      if (phoneVal.startsWith('+')) {
        if (!isValidPhoneNumber(phoneVal)) {
          return "Please enter a valid phone number";
        }
      } else {
        const digitsOnly = phoneVal.replace(/\D/g, '');
        if (digitsOnly.length < 8 || digitsOnly.length > 15) {
          return "Please enter a valid phone number";
        }
      }
    }

    // Type 3: Number
    if (fieldTypeId === 3 || field.type === 'number') {
      const baseFieldName = (field.fieldName || field.FieldName || '').toLowerCase();
      if (baseFieldName === 'abn_cont') {
        // ABN is formatted as string with spaces, accept it
        return null;
      }
      const numVal = Number(value);
      if (isNaN(numVal)) {
        return "Please enter a valid number";
      }
    }

    return null;
  };

  // Validate a single field
  const validateField = async (fieldId, label, value) => {
    const allFields = getAllFieldsFlat();

    const field = allFields.find(f => f.id === fieldId);
    if (!field) return;

    const errorMsg = validateFieldValue(field, value);

    setFormErrors((prev) => {
      const newErrors = { ...prev };
      if (errorMsg) {
        newErrors[fieldId] = errorMsg;
      } else {
        delete newErrors[fieldId];
      }
      return newErrors;
    });
  };

  const handleFieldChange = (fieldId, value) => {
    // Sanitize value to prevent HTML elements and events from being stored
    let sanitizedValue = value;

    if (typeof value === 'object' && value !== null) {
      // Check if it's a React SyntheticEvent
      if (value.nativeEvent && value.target && typeof value.preventDefault === 'function') {
        // Extract value from SyntheticEvent
        sanitizedValue = value.target.value;
        console.warn(`Field ${fieldId} received SyntheticEvent, extracted value:`, sanitizedValue);
      } else if (value instanceof Element || value instanceof Node) {
        // Extract value from HTML elements
        sanitizedValue = value.value || value.textContent || String(value);
        console.warn(`Field ${fieldId} received HTML element, extracted value:`, sanitizedValue);
      }
    }

    // Find the field to check its label for mobile number validation
    const field = getAllFieldsFlat().find(f => f.id === fieldId);

    // Format ABN field if applicable
    const lowerFieldName = (field?.fieldName || field?.FieldName || '').toLowerCase();
    const baseFieldName = lowerFieldName.replace(/_\d+$/, '');
    if (baseFieldName === 'abn_cont') {
      sanitizedValue = formatABN(sanitizedValue);
    }

    // Mobile number validation - only for NON-PhoneInput fields (the regular digit-only fields)
    const fieldTypeId = field?.fieldTypeId || field?.FieldTypeId;
    const isPhoneInputField = fieldId === phoneFieldId || fieldTypeId === 10 || field?.type === 'tel';

    if (!isPhoneInputField && field && fieldTypeId === 10 && sanitizedValue && typeof sanitizedValue === 'string') {
      // Remove non-digit characters and limit to 10 digits
      const digitsOnly = sanitizedValue.replace(/\D/g, '');
      if (digitsOnly.length > 10) {
        sanitizedValue = digitsOnly.slice(0, 10);
      } else {
        sanitizedValue = digitsOnly;
      }
    }

    // Reset phone verification if phone number changes
    if (fieldId === phoneFieldId && (isPhoneVerified || otpSent)) {
      setIsPhoneVerified(false);
      setOtpSent(false);
      setOtp('');
      setOtpId(null);
      setOtpError('');
    }

    setFormValues(prev => ({
      ...prev,
      [fieldId]: sanitizedValue
    }));

    // Trigger real-time validation if the field was previously invalid or has something changing
    if (field) {
      validateField(fieldId, field.label, sanitizedValue);
    }
  };

  // Validate a set of fields based on type and isMandatory rules
  const validateFormFields = async (fields) => {
    let isValid = true;
    const errors = {};

    for (const field of fields) {
      const value = formValues[field.id];
      const errorMsg = validateFieldValue(field, value);

      if (errorMsg) {
        errors[field.id] = errorMsg;
        isValid = false;
      }
    }

    setFormErrors(prev => {
      const newErrors = { ...prev };
      fields.forEach(field => {
        if (errors[field.id]) {
          newErrors[field.id] = errors[field.id];
        } else {
          delete newErrors[field.id];
        }
      });
      return newErrors;
    });

    return isValid;
  };

  const handleNextStep = async () => {
    if (form.isMultistep && form.steps[currentStep]) {
      // Validate current step's fields before moving to next
      const currentStepFields = getFlatFieldsWithSimulated(groupContactFields(form.steps[currentStep].fields || []), simulatedContacts);
      const isValid = await validateFormFields(currentStepFields);

      if (!isValid) {
        toast.error('Please Fill the Required Fields', {
          duration: 3000,
        });
        return;
      }
    }

    if (currentStep < form.steps.length - 1) {
      setCurrentStep(prev => prev + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handlePreviousStep = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleSubmit = async () => {
    // Validate all required fields before submission
    const allFields = getAllFieldsFlat();


    const isValid = await validateFormFields(allFields);
    if (!isValid) {
      toast.error('Please Fill the Required Fields', {
        duration: 3000,
      });
      return;
    }

    // Check if phone verification is required and not completed
    // if (phoneFieldId && !isPhoneVerified) {
    //   toast.error('Phone verification required', {
    //     description: 'Please verify your phone number before submitting the form.',
    //     duration: 5000,
    //   });
    //   return;
    // }

    setIsSubmitting(true);

    try {
      // Build contacts array
      const contacts = simulatedContacts.map((contactId, contactIdx) => {
        const getVal = (fieldName) => {
          const rawFields = form.isMultistep
            ? form.steps?.flatMap(step => step.fields) || []
            : form.fields || [];
          const grouped = groupContactFields(rawFields);
          const fieldset = grouped.find(f => f.type === 'contact_fieldset');
          const innerField = fieldset?.fields?.find(f => (f.fieldName || f.FieldName || '').toLowerCase() === fieldName.toLowerCase());
          if (!innerField) return "";

          const fieldId = contactIdx === 0 ? innerField.id : `${innerField.id}_${contactId}`;
          return formValues[fieldId] || "";
        };
        const abnVal = getVal("abn_cont") ? getVal("abn_cont").toString().replace(/\s+/g, '') : null;
        return {
          FullName: getVal("full_name_cont"),
          Email: getVal("email_cont"),
          PhoneNumber: getVal("phone_number_cont"),
          StreetAddress: getVal("street_address_cont"),
          Suburb: getVal("suburb_cont"),
          PostalCode: getVal("postal_code_cont"),
          State: getVal("state_cont"),
          Abn: abnVal,
          ABNNumber: abnVal,
          CompanyName: getVal("company_name_cont")
        };
      }).filter(c => c.FullName || c.PhoneNumber || c.Email || c.Abn || c.ABNNumber || c.CompanyName);

      // Remap formValues from integer FieldId keys → GUID MasterFieldId keys
      // so the API receives the same GUID-keyed fieldId as QuickLeadModal sends
      const remappedFormValues = {};
      allFields.forEach(field => {
        const submitKey = field.masterFieldId || field.id;
        const lowerFieldName = (field.fieldName || field.FieldName || '').toLowerCase();
        const baseFieldName = lowerFieldName.replace(/_\d+$/, '');
        if (CONTACT_FIELD_NAMES.includes(baseFieldName)) {
          return;
        }

        if (formValues[field.id] !== undefined) {
          remappedFormValues[submitKey] = formValues[field.id];
        }
      });

      remappedFormValues.contacts = contacts;

      const result = await submitFormWebhook(
        form.id,
        remappedFormValues,
        assignToUser,
        isQR ? 'QR' : 'Form',
        isQR ? qrToken : null
      );

      if (result.success) {
        // Navigate to the Thank You page, passing the leadId from API response
        const leadId = result.leadId || result.data?.leadId || '';
        navigate(`/form-thankyou/${leadId}`);
      } else {
        toast.error(result.message || 'Failed to submit form', {
          description: 'Please check your information and try again.',
          duration: 5000,
        });
      }
    } catch (err) {
      console.error('Submission error:', err);
      toast.error('Failed to submit form', {
        description: 'An unexpected error occurred. Please try again.',
        duration: 5000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderField = (field) => {
    const fieldValue = formValues[field.id] || (field.type === 'checkbox' ? [] : '');

    const fieldTypeId = field.fieldTypeId || field.FieldTypeId;

    return (
      <div key={field.id} className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">
          {field.label}
          {(field.required || field.isMandatory || field.IsMendatory) && <span className="text-red-500 ml-1">*</span>}
        </label>

        {/* Phone field - using PhoneInput with country code dropdown */}
        {(fieldTypeId === 10 || field.type === 'tel') ? (
          <div>
            <div className="relative">
              {(() => {
                // Find the Country field from the form schema to read its current value
                const allFields = form?.isMultistep
                  ? form.steps?.flatMap(step => step.fields) || []
                  : form?.fields || [];
                const countryField = allFields.find(f => {
                  const lower = f.label?.toLowerCase() || '';
                  return lower === 'country' || lower === 'countryid';
                });
                const countryValue = countryField ? (formValues[countryField.id] || 'Australia') : 'Australia';
                // Maps country names to codes
                const countryNameToCode = Object.entries(enLocale).reduce((acc, [code, name]) => {
                  acc[name.toLowerCase()] = code;
                  return acc;
                }, {});
                const countryCode = countryNameToCode[String(countryValue).toLowerCase()] || 'AU';
                const currentVal = fieldValue || (countryCode === 'AU' ? '+61' : '');

                return (
                  <PhoneInput
                    international
                    country={countryCode}
                    defaultCountry="AU"
                    value={currentVal}
                    onChange={(value) => {
                      let val = value || '';
                      if (countryCode === 'AU' && !val.startsWith('+61')) {
                        val = '+61';
                      }
                      handleFieldChange(field.id, val);
                    }}
                    onCountryChange={(newCountryCode) => {
                      if (newCountryCode && enLocale[newCountryCode]) {
                        const countryName = enLocale[newCountryCode];
                        let matchedFieldId = null;
                        let matchedValue = null;

                        const cField = allFields.find(f => {
                          const lower = f.label?.toLowerCase() || '';
                          return lower === 'country' || lower === 'countryid';
                        });

                        if (cField) {
                          matchedFieldId = cField.id;
                          const opts = cField.options || [];
                          const opt = opts.find(o =>
                            (o.label || '').toLowerCase() === countryName.toLowerCase()
                          );
                          matchedValue = opt ? opt.value : countryName;
                        }

                        if (matchedFieldId && matchedValue) {
                          handleFieldChange(matchedFieldId, matchedValue);
                        }
                      }
                    }}
                    placeholder={field.placeholder || 'Enter phone number'}
                    className="phone-input-custom"
                    style={{
                      '--PhoneInputCountryFlag-borderRadius': form.styling?.borderRadius || '4px',
                    }}
                    numberInputProps={{
                      className: "w-full bg-transparent border-none focus:ring-0 p-0 text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none",
                      onKeyDown: (e) => {
                        if (countryCode !== 'AU') return;
                        const start = e.target.selectionStart;
                        const end = e.target.selectionEnd;

                        if (e.key === 'Backspace') {
                          if (start === end && start <= 3) {
                            e.preventDefault();
                            return;
                          }
                          if (start !== end && start < 3) {
                            e.preventDefault();
                            return;
                          }
                        }
                        if (e.key === 'Delete') {
                          if (start === end && start < 3) {
                            e.preventDefault();
                            return;
                          }
                          if (start !== end && start < 3) {
                            e.preventDefault();
                            return;
                          }
                        }
                        if (start < 3 && start !== end && !e.ctrlKey && !e.metaKey && e.key.length === 1) {
                          e.preventDefault();
                        }
                      },
                      onPaste: (e) => {
                        if (countryCode !== 'AU') return;
                        const start = e.target.selectionStart;
                        if (start < 3) {
                          e.preventDefault();
                        }
                      },
                      onCut: (e) => {
                        if (countryCode !== 'AU') return;
                        const start = e.target.selectionStart;
                        const end = e.target.selectionEnd;
                        if (start < 3 || end < 3) {
                          e.preventDefault();
                        }
                      }
                    }}
                  />
                );
              })()}
            </div>
          </div>
        ) : (field.type === 'text' || field.type === 'email' || field.type === 'number' || field.type === 'date' || field.type === 'password') ? (
          <div>
            {/* Regular text/email/number/date/password fields */}
            <Input
              type={((field.fieldName || field.FieldName || '').toLowerCase() === 'abn_cont') ? 'text' : field.type}
              value={fieldValue}
              onChange={(e) => handleFieldChange(field.id, e.target.value)}
              placeholder={field.placeholder}
              required={field.required}
              className="w-full"
              style={{ borderRadius: form.styling?.borderRadius || '8px' }}
            />
          </div>
        ) : null}

        {field.type === 'textarea' && (
          <Textarea
            value={fieldValue}
            onChange={(e) => handleFieldChange(field.id, e.target.value)}
            placeholder={field.placeholder}
            required={field.required}
            rows={4}
            className="w-full"
            style={{ borderRadius: form.styling?.borderRadius || '8px' }}
          />
        )}

        {field.type === 'select' && (
          <ReactSelect
            value={field.options?.find(opt => opt.value === fieldValue) || null}
            onChange={(selectedOption) => {
              handleFieldChange(field.id, selectedOption ? selectedOption.value : '');
            }}
            placeholder={`Select ${field.label}`}
            options={field.options}
            isClearable={!field.required}
            className="react-select-container w-full"
            classNamePrefix="react-select"
            styles={{
              control: (baseStyles, state) => ({
                ...baseStyles,
                borderRadius: form.styling?.borderRadius || '8px',
                borderColor: state.isFocused 
                  ? (form.styling?.primaryColor || '#3B82F6') 
                  : '#D1D5DB',
                boxShadow: state.isFocused 
                  ? `0 0 0 1px ${form.styling?.primaryColor || '#3B82F6'}` 
                  : 'none',
                minHeight: '42px',
                '&:hover': {
                  borderColor: state.isFocused 
                    ? (form.styling?.primaryColor || '#3B82F6') 
                    : '#9CA3AF'
                }
              }),
              menu: (baseStyles) => ({
                ...baseStyles,
                borderRadius: form.styling?.borderRadius || '8px',
                zIndex: 50
              }),
              option: (baseStyles, { isFocused, isSelected }) => ({
                ...baseStyles,
                backgroundColor: isSelected 
                  ? (form.styling?.primaryColor || '#3B82F6') 
                  : isFocused 
                    ? `${form.styling?.primaryColor || '#3B82F6'}20` 
                    : 'white',
                color: isSelected ? 'white' : '#374151',
                '&:active': {
                  backgroundColor: form.styling?.primaryColor || '#3B82F6',
                  color: 'white'
                }
              })
            }}
          />
        )}

        {field.type === 'checkbox' && (
          <div className="space-y-2 p-1">
            {field.options?.map(option => {
              const isChecked = fieldValue.includes(option.value);
              return (
                <label key={option.id} className="flex items-center cursor-pointer hover:bg-gray-50 p-2 rounded-md transition-colors duration-150">
                  <div className="relative mr-3">
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={(e) => {
                        const newValues = e.target.checked
                          ? [...fieldValue, option.value]
                          : fieldValue.filter(v => v !== option.value);
                        handleFieldChange(field.id, newValues);
                      }}
                      className="sr-only"
                    />
                    <div className={`w-5 h-5 rounded border-2 transition-all duration-200 cursor-pointer flex items-center justify-center ${isChecked
                      ? 'border-blue-600'
                      : 'bg-white border-gray-300 hover:border-blue-400'
                      }`}
                      style={{
                        backgroundColor: isChecked ? (form.styling?.primaryColor || '#3B82F6') : 'white',
                        borderColor: isChecked ? (form.styling?.primaryColor || '#3B82F6') : undefined
                      }}>
                      {isChecked && (
                        <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </div>
                  </div>
                  <span className="text-sm font-medium select-none" style={{ color: form.styling?.textColor || '#374151' }}>
                    {option.label}
                  </span>
                </label>
              );
            })}
          </div>
        )}

        {field.type === 'radio' && (
          <div className="space-y-2 p-1">
            {field.options?.map(option => (
              <label key={option.id} className="flex items-center cursor-pointer hover:bg-gray-50 p-2 rounded-md transition-colors duration-150">
                <div className="relative mr-3">
                  <input
                    type="radio"
                    name={`field-${field.id}`}
                    value={option.value}
                    checked={fieldValue === option.value}
                    onChange={(e) => handleFieldChange(field.id, e.target.value)}
                    className="sr-only"
                  />
                  <div className={`w-5 h-5 rounded-full border-2 transition-all duration-200 cursor-pointer flex items-center justify-center ${fieldValue === option.value
                    ? 'border-blue-600'
                    : 'bg-white border-gray-300 hover:border-blue-400'
                    }`}
                    style={{
                      backgroundColor: fieldValue === option.value ? (form.styling?.primaryColor || '#3B82F6') : 'white',
                      borderColor: fieldValue === option.value ? (form.styling?.primaryColor || '#3B82F6') : undefined
                    }}>
                    {fieldValue === option.value && (
                      <div className="w-2 h-2 bg-white rounded-full"></div>
                    )}
                  </div>
                </div>
                <span className="text-sm font-medium select-none" style={{ color: form.styling?.textColor || '#374151' }}>
                  {option.label}
                </span>
              </label>
            ))}
          </div>
        )}

        {field.type === 'attachments' && (
          <div className="relative">
            <AttachmentsActions
              files={fieldValue || []}
              onChange={(files) => handleFieldChange(field.id, files)}
              addLabel={field.label || "Upload Files"}
              showLabel={false}
              className="w-full"
            />
          </div>
        )}

        {field.type === 'editor' && (
          <div className="relative">
            <RichTextEditor
              value={fieldValue}
              onChange={(value) => handleFieldChange(field.id, value)}
              placeholder={field.placeholder || "Enter rich text content..."}
              height="250px"
            />
          </div>
        )}

        {/* Error Message */}
        {formErrors[field.id] && (
          <p className="text-red-500 text-xs mt-1 animate-fadeIn flex items-center gap-1">
            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            {formErrors[field.id]}
          </p>
        )}
      </div>
    );
  };

  // Loading state removed - now handled by global loader

  if (error) {
    // Different styling for different error types
    const isInactive = error === 'This form is currently unavailable';
    const isAccessDenied = error === 'access_denied';

    let iconColor, bgColor, title, message, helpText;

    if (isAccessDenied) {
      iconColor = 'text-red-600';
      bgColor = 'bg-red-100';
      title = 'Access Denied';
      message = 'You are not authorized to view this form from this domain.';
      helpText = 'Please contact the form owner to authorize your domain or ensure you are accessing the form from the correct website.';
    } else if (isInactive) {
      iconColor = 'text-orange-600';
      bgColor = 'bg-orange-100';
      title = 'Form Unavailable';
      message = error;
      helpText = 'Please contact the form owner for more information.';
    } else {
      iconColor = 'text-red-600';
      bgColor = 'bg-red-100';
      title = 'Form Not Found';
      message = error;
      helpText = null;
    }

    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center form-page-container">
        <div className="text-center max-w-md mx-auto px-4">
          <div className={`w-16 h-16 ${bgColor} rounded-full flex items-center justify-center mx-auto mb-4`}>
            {isAccessDenied ? (
              <svg className={`w-8 h-8 ${iconColor}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m0 0h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            ) : isInactive ? (
              <svg className={`w-8 h-8 ${iconColor}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728L5.636 5.636m12.728 12.728L18.364 5.636M5.636 18.364l12.728-12.728" />
              </svg>
            ) : (
              <svg className={`w-8 h-8 ${iconColor}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            )}
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">{title}</h2>
          <p className="text-gray-600 mb-3">{message}</p>
          {helpText && (
            <p className="text-sm text-gray-500">{helpText}</p>
          )}
        </div>
      </div>
    );
  }


  if (!form) {
    return null;
  }

  const currentStepData = form.isMultistep ? form.steps[currentStep] : null;
  const fieldsToRender = form.isMultistep ? currentStepData?.fields || [] : form.fields || [];

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 form-page-container">
      <div className="max-w-2xl mx-auto">
        <div
          className="bg-white rounded-lg shadow-lg p-8"
          style={{
            backgroundColor: form.styling?.backgroundColor || '#ffffff',
            borderRadius: form.styling?.borderRadius || '8px',
            color: form.styling?.textColor || '#1f2937'
          }}
        >
          {/* Form Header */}
          <div className="mb-8">
            <h1
              className="text-3xl font-bold mb-4"
              style={{ color: form.styling?.primaryColor || '#3B82F6' }}
            >
              {form.title}
            </h1>
            {form.description && (
              <p className="text-gray-600 mb-6">{form.description}</p>
            )}

            {/* Progress Bar for Multistep */}
            {form.isMultistep && (
              <div className="mb-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium">{currentStepData?.stepTitle}</span>
                  <span className="text-xs text-gray-500">Step {currentStep + 1} of {form.steps.length}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="h-2 rounded-full transition-all duration-300"
                    style={{
                      width: `${((currentStep + 1) / form.steps.length) * 100}%`,
                      backgroundColor: form.styling?.primaryColor || '#3B82F6'
                    }}
                  />
                </div>
                {currentStepData?.stepDescription && (
                  <p className="text-sm text-gray-600 mt-2">{currentStepData.stepDescription}</p>
                )}
              </div>
            )}
          </div>

          {/* Form Fields */}
          <div className="space-y-6">
            {groupContactFields(fieldsToRender).map((field, fieldIdx) => {
              if (field.type === 'contact_fieldset') {
                const primaryColor = form.styling?.primaryColor || '#3B82F6';
                const borderRadius = form.styling?.borderRadius || '8px';

                return (
                  <div
                    key={field.id || fieldIdx}
                    className="border p-5 space-y-4"
                    style={{
                      borderColor: `${primaryColor}50`,
                      borderRadius: borderRadius,
                      backgroundColor: `${primaryColor}05`
                    }}
                  >
                    <div className="flex items-center justify-between pb-2 border-b" style={{ borderColor: `${primaryColor}20` }}>
                      <span className="text-sm font-semibold flex items-center gap-1.5" style={{ color: primaryColor }}>
                        👤 Contact Information
                      </span>
                    </div>

                    {simulatedContacts.map((contactId, contactIdx) => (
                      <div key={contactId} className="space-y-4 pb-4 border-b last:border-0 last:pb-0" style={{ borderColor: `${primaryColor}15` }}>
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                            Contact {contactIdx + 1}
                          </span>
                          {contactIdx > 0 && (
                            <button
                              type="button"
                              className="text-xs text-red-500 hover:text-red-700 font-medium cursor-pointer"
                              onClick={() => {
                                setSimulatedContacts(prev => prev.filter(id => id !== contactId));
                                // Clean up formValues and formErrors for the removed contact
                                if (field.fields && Array.isArray(field.fields)) {
                                  const keysToRemove = field.fields.map(innerF => `${innerF.id}_${contactId}`);
                                  setFormValues(prev => {
                                    const updated = { ...prev };
                                    keysToRemove.forEach(k => delete updated[k]);
                                    return updated;
                                  });
                                  setFormErrors(prev => {
                                    const updated = { ...prev };
                                    keysToRemove.forEach(k => delete updated[k]);
                                    return updated;
                                  });
                                }
                              }}
                            >
                              Remove
                            </button>
                          )}
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          {field.fields?.map((innerField, innerIdx) => {
                            const fieldId = contactIdx === 0 ? innerField.id : `${innerField.id}_${contactId}`;
                            const fieldToRender = {
                              ...innerField,
                              id: fieldId
                            };
                            return (
                              <div key={innerField.id || innerIdx}>
                                {renderField(fieldToRender)}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}

                    <div className="pt-2 border-t" style={{ borderColor: `${primaryColor}20` }}>
                      <button
                        type="button"
                        className="flex items-center gap-1 py-1 px-3 text-xs border rounded transition-colors text-gray-700 dark:text-gray-200 cursor-pointer"
                        style={{
                          color: primaryColor,
                          borderColor: primaryColor,
                          borderRadius: borderRadius,
                          backgroundColor: 'transparent'
                        }}
                        onClick={(e) => {
                          e.preventDefault();
                          setSimulatedContacts(prev => [...prev, Math.max(...prev, 0) + 1]);
                        }}
                      >
                        <span>+ Add Another Contact</span>
                      </button>
                    </div>
                  </div>
                );
              }

              return renderField(field);
            })}
          </div>

          {/* Navigation Buttons */}
          <div className="flex justify-between mt-8">
            {form.isMultistep && currentStep > 0 && (
              <button
                onClick={handlePreviousStep}
                className="px-4 py-2 text-gray-600 border border-gray-300 font-medium rounded-md hover:bg-gray-50 transition-colors cursor-pointer"
                style={{ borderRadius: form.styling?.borderRadius || "8px" }}
              >
                Previous
              </button>
            )}

            <div className="flex-1" />

            {form.isMultistep ? (
              currentStep < form.steps.length - 1 ? (
                <button
                  onClick={handleNextStep}
                  className="px-4 py-2 text-white font-medium rounded-md hover:opacity-90 transition-opacity cursor-pointer"
                  style={{
                    backgroundColor: form.styling?.primaryColor || "#3B82F6",
                    borderRadius: form.styling?.borderRadius || "8px"
                  }}
                >
                  Next
                </button>
              ) : (
                <div className="flex flex-col items-end gap-2">
                  {/* {phoneFieldId && !isPhoneVerified && (
                    <p className="text-xs text-orange-600">Please verify your phone number to submit</p>
                  )} */}
                  <button
                    onClick={handleSubmit}
                    // disabled={isSubmitting || (phoneFieldId && !isPhoneVerified)}
                    disabled={isSubmitting}
                    className="px-4 py-2 text-white font-medium rounded-md hover:opacity-90 transition-opacity disabled:opacity-50 cursor-pointer disabled:cursor-not-allowed"
                    style={{
                      backgroundColor: form.styling?.primaryColor || "#3B82F6",
                      borderRadius: form.styling?.borderRadius || "8px"
                    }}
                  >
                    {isSubmitting ? 'Submitting...' : 'Submit Form'}
                  </button>
                </div>
              )
            ) : (
              <div className="flex flex-col items-end gap-2">
                {/* {phoneFieldId && !isPhoneVerified && (
                  <p className="text-xs text-orange-600">Please verify your phone number to submit</p>
                )} */}
                <button
                  onClick={handleSubmit}
                  // disabled={isSubmitting || (phoneFieldId && !isPhoneVerified)}
                  disabled={isSubmitting}
                  className="px-4 py-2 text-white font-medium rounded-md hover:opacity-90 transition-opacity disabled:opacity-50 cursor-pointer disabled:cursor-not-allowed"
                  style={{
                    backgroundColor: form.styling?.primaryColor || "#3B82F6",
                    borderRadius: form.styling?.borderRadius || "8px"
                  }}
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Form'}
                </button>
              </div>
            )}
          </div>

          {/* Branding */}
          <div className="mt-8 pt-4 border-t border-gray-200 text-center">
            <p className="text-[14px] text-gray-500 flex justify-center items-center gap-2">
              Powered by  <img src={companyLogo} className="h-8" alt="logo" onError={(e) => { e.target.src = sunnyHomesCrmLogo }} />
            </p>
          </div>
        </div>
      </div>

      {/* OTP Verification Modal - Using Headless UI Dialog */}
      <Transition appear show={showOtpModal} as={Fragment}>
        <Dialog
          as="div"
          className="fixed inset-0 z-100 flex flex-col items-center justify-center overflow-hidden px-4 py-6 sm:px-5"
          onClose={() => {

          }}
        >
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="absolute inset-0 bg-gray-900/50 transition-opacity dark:bg-black/40" />
          </TransitionChild>

          <TransitionChild
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <DialogPanel className="scrollbar-sm relative flex w-full max-w-md flex-col overflow-y-auto rounded-lg bg-white px-6 py-8 text-center transition-all duration-300 dark:bg-dark-700">
              {/* Close button */}
              <button
                type="button"
                onClick={() => {
                  setShowOtpModal(false);
                  setOtpError('');
                }}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 dark:text-dark-300 dark:hover:text-dark-100 cursor-pointer"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>

              {/* Modal Header */}
              <div className="mb-6">
                <div className="w-14 h-14 rounded-full bg-primary-100 dark:bg-primary-500/20 flex items-center justify-center mx-auto mb-4">
                  <svg
                    className="w-7 h-7 text-primary-600 dark:text-primary-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                </div>
                <DialogTitle
                  as="h3"
                  className="text-xl font-semibold text-gray-800 dark:text-dark-100"
                >
                  Verify Phone Number
                </DialogTitle>
                <p className="mt-2 text-sm text-gray-500 dark:text-dark-300">
                  Enter the OTP sent to your phone
                </p>
              </div>

              {/* OTP Input */}
              <div className="space-y-4">
                <Input
                  type="text"
                  value={otp}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                    setOtp(value);
                    setOtpError('');
                  }}
                  placeholder="Enter OTP"
                  className="w-full text-center text-lg tracking-widest"
                  maxLength={6}
                  autoFocus
                />

                {otpError && (
                  <p className="text-sm text-error dark:text-error-lighter text-center">{otpError}</p>
                )}

                {/* Verify Button */}
                <Button
                  onClick={handleVerifyOtp}
                  disabled={isVerifyingOtp || !otp || otp.length < 4}
                  className="w-full"
                  color="primary"
                >
                  {isVerifyingOtp ? 'Verifying...' : 'Verify OTP'}
                </Button>

                {/* Resend OTP */}
                <div className="text-center">
                  <p className="text-sm text-gray-500 dark:text-dark-300">
                    Didn&apos;t receive the code?{' '}
                    {canResendOtp ? (
                      <button
                        type="button"
                        onClick={handleSendOtp}
                        disabled={isSendingOtp}
                        className="font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 hover:underline cursor-pointer disabled:opacity-50"
                      >
                        {isSendingOtp ? 'Sending...' : 'Resend OTP'}
                      </button>
                    ) : (
                      <span className="text-gray-400 dark:text-dark-400">Resend in {resendTimer}s</span>
                    )}
                  </p>
                </div>
              </div>
            </DialogPanel>
          </TransitionChild>
        </Dialog>
      </Transition>
    </div>
  );
}
